import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Types for wizard state
export type CompanyCountry = 'US' | 'IN' | 'UK';

export interface CompanyData {
  companyId?: string;
  name: string;
  ticker?: string;
  industry?: string;
  description?: string;
  country?: CompanyCountry;
  cik?: string;            // US (SEC CIK)
  scId?: string;           // India (Moneycontrol stock code)
  companyNumber?: string;  // UK (Companies House 8-digit)
  financialData?: {
    revenue?: string;
    revenueGrowth?: string;
    grossMargin?: string;
    operatingMargin?: string;
    segments?: Array<{
      name: string;
      percentage?: number;
    }>;
  };
}

export interface IndustryData {
  industryId?: string;
  name: string;
  classification?: string;
  marketSizeUSD?: number;
  marketGrowthRate?: number;
  valueChain?: string[];
  technologyTrends?: string[];
  regulatoryEnvironment?: string;
  commonPainPoints?: string[];
  benchmarks?: {
    gross_margin?: number;
    operating_margin?: number;
    roe?: number;
    revenue_growth?: number;
  };
}

export interface CompetitorData {
  competitorId?: string;
  name: string;
  description?: string;
  market_position?: string;
  competitive_advantages?: string[];
  market_share?: number;
  financials?: {
    revenue?: number;
    gross_margin?: number;
    operating_margin?: number;
    revenue_growth?: number;
    fiscal_period?: string;
  };
  selected?: boolean; // For UI selection
  /** Registry identifiers.  The backend's `market_leader_to_wizard_competitor`
   *  already emits `companyId` / `cik` / `ticker` for market leaders it resolved
   *  to Company Research, and Step 3's registry-backed "Add Competitor" sets the
   *  same fields for manually added peers.  Declaring them keeps a manually
   *  added competitor indistinguishable from a generated one downstream (peer
   *  financial lookups, "View Company Research" links). */
  companyId?: string;
  country?: CompanyCountry;
  ticker?: string;
  cik?: string;          // US  (SEC CIK)
  scId?: string;         // India (Moneycontrol stock code)
  companyNumber?: string; // UK (Companies House 8-digit)
  weaknesses?: string[];
}

export interface QuestionDataSource {
  /** Source category — drives chip color/icon in the UI.  Known values:
   * 'focal_financial' | 'peer_avg' | 'industry_metric' | 'segment' |
   * 'ev_driver' | 'news_event' | 'regulation' | 'disruption' | 'technology' */
  type: string;
  label: string;
  value: string;
}

export interface QuestionData {
  questionId?: string;
  personaRole?: string;
  question: string;
  context?: string;
  priority?: 'Critical' | 'High' | 'Medium';
  category?: string;
  estimatedImpact?: string;
  selected?: boolean; // For UI selection
  /** Provenance chips emitted by the persona agent — rendered under the
   * question text so consultants can defend the question's data trail. */
  dataSources?: QuestionDataSource[];
}

export type WizardStep = 1 | 2 | 3 | 4 | 5;

interface WizardState {
  // Current step
  currentStep: WizardStep;

  // Data from each step
  companyData: CompanyData | null;
  industryData: IndustryData | null;
  competitorsData: CompetitorData[];
  questionsData: QuestionData[];

  // Case metadata
  caseName: string;
  caseDescription: string;

  // Actions
  setCurrentStep: (step: WizardStep) => void;
  nextStep: () => void;
  previousStep: () => void;

  setCompanyData: (data: CompanyData) => void;
  setIndustryData: (data: IndustryData) => void;
  setCompetitorsData: (data: CompetitorData[]) => void;
  setQuestionsData: (data: QuestionData[]) => void;

  setCaseName: (name: string) => void;
  setCaseDescription: (description: string) => void;

  // Toggle selections
  toggleCompetitor: (competitorId: string) => void;
  toggleQuestion: (questionId: string) => void;

  // Clear data
  clearQuestionsData: () => void;

  // Load wizard data
  loadWizardData: (wizardData: any) => void;

  // Reset wizard
  resetWizard: () => void;
}

const initialState = {
  currentStep: 1 as WizardStep,
  companyData: null,
  industryData: null,
  competitorsData: [],
  questionsData: [],
  caseName: '',
  caseDescription: '',
};

export const useWizardStore = create<WizardState>()(
  persist(
    (set) => ({
  ...initialState,

  // Step navigation
  setCurrentStep: (step) => set({ currentStep: step }),

  nextStep: () => set((state) => ({
    currentStep: Math.min(state.currentStep + 1, 5) as WizardStep
  })),

  previousStep: () => set((state) => ({
    currentStep: Math.max(state.currentStep - 1, 1) as WizardStep
  })),

  // Data setters
  // When the company identity changes, also wipe everything derived from it
  // (industry research, competitors, questions). Otherwise stale data from a
  // previous case shows up for the new one and no fresh lookup ever fires.
  // `loadWizardData` is the path used by edit-mode and must not go through here.
  setCompanyData: (data) => set((state) => {
    const prev = state.companyData;
    const prevKey =
      prev?.companyId ||
      prev?.cik ||
      prev?.scId ||
      prev?.companyNumber ||
      prev?.name?.toLowerCase();
    const nextKey =
      data?.companyId ||
      data?.cik ||
      data?.scId ||
      data?.companyNumber ||
      data?.name?.toLowerCase();
    const identityChanged = !!prev && prevKey !== nextKey;
    if (identityChanged) {
      return {
        companyData: data,
        industryData: null,
        competitorsData: [],
        questionsData: [],
      };
    }
    return { companyData: data };
  }),

  setIndustryData: (data) => set({ industryData: data }),

  setCompetitorsData: (data) => set({
    competitorsData: data.map(c => ({ ...c, selected: c.selected ?? true }))
  }),

  setQuestionsData: (data) => set({
    questionsData: data.map(q => ({ ...q, selected: q.selected ?? true }))
  }),

  setCaseName: (name) => set({ caseName: name }),

  setCaseDescription: (description) => set({ caseDescription: description }),

  // Toggle selections
  toggleCompetitor: (competitorId) => set((state) => ({
    competitorsData: state.competitorsData.map(c =>
      c.competitorId === competitorId ? { ...c, selected: !c.selected } : c
    )
  })),

  toggleQuestion: (questionId) => set((state) => ({
    questionsData: state.questionsData.map(q =>
      q.questionId === questionId ? { ...q, selected: !q.selected } : q
    )
  })),

  // Load wizard data (for edit mode)
  loadWizardData: (wizardData: any) => set({
    companyData: wizardData.companyData || null,
    industryData: wizardData.industryData || null,
    competitorsData: wizardData.competitorsData || [],
    questionsData: wizardData.questionsData || [],
    caseName: wizardData.caseName || '',
    caseDescription: wizardData.caseDescription || ''
  }),

  // Clear specific data
  clearQuestionsData: () => set({ questionsData: [] }),

  // Reset
  resetWizard: () => set(initialState),
}),
    {
      name: 'case-wizard-state',
      storage: createJSONStorage(() => localStorage),
      // Only persist data fields — not actions or transient UI state
      partialize: (state) => ({
        currentStep: state.currentStep,
        companyData: state.companyData,
        industryData: state.industryData,
        competitorsData: state.competitorsData,
        questionsData: state.questionsData,
        caseName: state.caseName,
        caseDescription: state.caseDescription,
      }),
    }
  )
);
